import React, { useContext, useEffect, useRef, useState } from 'react';
import Layout from 'components/sales/layout/layout';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { getDarwinDailyMeetings } from 'services/darwinRecord/darwinRecordApi';
import { REGULARIZATION_STATUS } from './constants';
import {
  formatDisplayDate,
  formatHeaderDate,
  formatTime,
  getRecordStatusClass,
  isFutureRecordDate,
  parseQueryParams,
  darwinRecordListPath,
} from './darwinRecordUtils';
import { isValidRecordDate } from './darwinRecordValidation';
import './darwinRecord.css';

const DarwinDailyActivities = ({ history, location }) => {
  const { baseUrl } = useContext(BaseUrl);
  const query = parseQueryParams(location?.search);
  const { date, status } = query;

  const [activities, setActivities] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const hadValidDateRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    if (!date || !isValidRecordDate(date)) {
      // Date can briefly clear during route transitions; skip toast if we already validated.
      if (hadValidDateRef.current) {
        return () => {
          isActive = false;
        };
      }
      toast.error('Invalid or missing date');
      history.replace(darwinRecordListPath(baseUrl));
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    if (isFutureRecordDate(date)) {
      toast.error('View details is not available for future dates');
      history.replace(darwinRecordListPath(baseUrl));
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    hadValidDateRef.current = true;

    const fetchDetails = async () => {
      setIsLoading(true);
      const { data, error } = await getDarwinDailyMeetings({ date });
      if (!isActive) return;
      if (error) {
        toast.error(error);
        setActivities([]);
        setRegularizations([]);
      } else {
        setActivities(data?.activities || []);
        setRegularizations(data?.regularization || []);
      }
      setIsLoading(false);
    };

    fetchDetails();

    return () => {
      isActive = false;
    };
  }, [date, baseUrl, history]);

  const statusClass = getRecordStatusClass(status);
  const pageTitle = date ? formatHeaderDate(date) : 'Daily Activities';
  const isOutOfOffice = (moduleType) => moduleType === 'Out Of Office';

  const renderActivityCard = (activityItem, index) => {
    const statusMeta =
      REGULARIZATION_STATUS[activityItem.regularizationStatus] || {
        text: 'Unknown',
        className: 'darwin-status-default',
      };
    const showApprovalStatus = isOutOfOffice(activityItem.moduleType);

    return (
      <div key={`activity-${index}`} className="darwin-activity-card">
        <div className="darwin-activity-card__header">
          <h4 className="darwin-activity-card__title">Meeting {index + 1}</h4>
          {showApprovalStatus ? (
            <span className={`darwin-status-badge ${statusMeta.className}`}>
              {statusMeta.text}
            </span>
          ) : (
            <span className="darwin-activity-card__type">{activityItem.moduleType}</span>
          )}
        </div>

        <div className="darwin-activity-card__rows">
          {activityItem.subject ? (
            <div>
              <span className="darwin-activity-card__row-label">Subject</span>
              <div className="darwin-activity-card__row-value">{activityItem.subject}</div>
            </div>
          ) : null}
          <div>
            <span className="darwin-activity-card__row-label">Timing</span>
            <div className="darwin-activity-card__row-value">
              {formatTime(activityItem.checkInTime)}
              {activityItem.checkOutTime
                ? ` → ${formatTime(activityItem.checkOutTime)}`
                : null}
            </div>
          </div>
          {activityItem.officeLocation ? (
            <div>
              <span className="darwin-activity-card__row-label">Location</span>
              <div className="darwin-activity-card__row-value">
                {activityItem.officeLocation}
              </div>
            </div>
          ) : null}
          {activityItem.body ? (
            <div>
              <span className="darwin-activity-card__row-label">Reason</span>
              <div className="darwin-activity-card__row-value">{activityItem.body}</div>
            </div>
          ) : null}
        </div>

        {showApprovalStatus && activityItem.regularizationStatus === 2 ? (
          <div className="darwin-activity-card__footer">
            <div className="darwin-remark-box">
              <div className="darwin-remark-box__title">Manager Remark</div>
              <p className="darwin-remark-box__text">
                {activityItem.regularizationRemarks}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderRegularizationCard = (item, index) => {
    const statusMeta = REGULARIZATION_STATUS[item.status] || {
      text: 'Unknown',
      className: 'darwin-status-default',
    };

    return (
      <div key={`regularization-${index}`} className="darwin-activity-card">
        <div className="darwin-activity-card__header">
          <h4 className="darwin-activity-card__title">Regularization {index + 1}</h4>
          <span className={`darwin-status-badge ${statusMeta.className}`}>
            {statusMeta.text}
          </span>
        </div>

        <div className="darwin-activity-card__rows">
          <div>
            <span className="darwin-activity-card__row-label">Date</span>
            <div className="darwin-activity-card__row-value">
              {formatDisplayDate(item.regularizationDate)}
            </div>
          </div>
          <div>
            <span className="darwin-activity-card__row-label">Timing</span>
            <div className="darwin-activity-card__row-value">
              {formatTime(item.checkInTime)} → {formatTime(item.checkOutTime)}
            </div>
          </div>
          {item.reasonForMissedAttendance ? (
            <div>
              <span className="darwin-activity-card__row-label">Reason</span>
              <div className="darwin-activity-card__row-value">
                {item.reasonForMissedAttendance}
              </div>
            </div>
          ) : null}
          {item.message ? (
            <div>
              <span className="darwin-activity-card__row-label">Message</span>
              <div className="darwin-activity-card__row-value">{item.message}</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <Layout
      title={pageTitle}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={darwinRecordListPath(baseUrl)}
    >
      <div className="darwin-record-page">
        {status ? (
          <div className="darwin-daily-header">
            <span className={`darwin-status-badge ${statusClass}`}>{status}</span>
          </div>
        ) : null}

        <h3 className="darwin-section-title">Meetings</h3>
        {activities.length === 0 ? (
          <div className="darwin-empty-state" style={{ minHeight: 160 }}>
            <p className="darwin-empty-state__subtitle">
              No activities found for this date.
            </p>
          </div>
        ) : (
          <div className="darwin-record-grid">
            {activities.map(renderActivityCard)}
          </div>
        )}

        {regularizations.length > 0 ? (
          <>
            <h3 className="darwin-section-title">Regularization</h3>
            <div className="darwin-record-grid">
              {regularizations.map(renderRegularizationCard)}
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default DarwinDailyActivities;
